'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Minus, Plus, Search, ShoppingBag, X } from 'lucide-react';

import { useEffect, useState } from 'react';

export interface SelectedItem {
    item: App.Data.MenuItemData;
    quantity: number;
}

interface MenuItemPickerProps {
    selectedItems: SelectedItem[];
    onItemsChange: (items: SelectedItem[]) => void;
    menuItems?: App.Data.MenuItemData[];
    categories?: Array<string>;
}

export function MenuItemPicker({
    selectedItems,
    onItemsChange,
    menuItems: propsMenuItems = [],
    categories: propsCategories = [],
}: MenuItemPickerProps) {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState('All');
    const [menuItems, setMenuItems] =
        useState<App.Data.MenuItemData[]>(propsMenuItems);
    const [categories, setCategories] =
        useState<Array<string>>(propsCategories);
    const [loading, setLoading] = useState(false);
    const [initialLoad, setInitialLoad] = useState(propsMenuItems.length === 0);

    // Fetch categories on mount if not provided
    useEffect(() => {
        if (propsCategories.length === 0 && open) {
            fetch('/api/menu-items-api/categories')
                .then((res) => res.json())
                .then((data) => {
                    if (data.data) {
                        setCategories(data.data);
                    }
                })
                .catch(console.error);
        } else if (propsCategories.length > 0) {
            setCategories(propsCategories);
        }
    }, [open, propsCategories]);

    // Debounced search and category filter
    useEffect(() => {
        if (!open) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setLoading(true);
            const params = new URLSearchParams({
                status: 'active',
                per_page: '100',
            });

            if (searchQuery.trim()) {
                params.append('q', searchQuery.trim());
            }

            if (activeCategory && activeCategory !== 'All') {
                params.append('category', activeCategory);
            }

            fetch(`/api/menu-items-api?${params.toString()}`)
                .then((res) => res.json())
                .then((data) => {
                    if (data.data) {
                        setMenuItems(data.data);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching menu items:', error);
                    setLoading(false);
                });
        }, 300); // 300ms debounce

        return () => clearTimeout(timeoutId);
    }, [searchQuery, activeCategory, open]);

    // Initial load when dialog opens
    useEffect(() => {
        if (open && initialLoad) {
            setLoading(true);
            fetch('/api/menu-items-api?status=active&per_page=100')
                .then((res) => res.json())
                .then((data) => {
                    if (data.data) {
                        setMenuItems(data.data);
                    }
                    setLoading(false);
                    setInitialLoad(false);
                })
                .catch((error) => {
                    console.error('Error fetching menu items:', error);
                    setLoading(false);
                });
        }
    }, [open, initialLoad]);

    const getItemQuantity = (itemId: string) => {
        const found = selectedItems.find((si) => si.item.id === itemId);
        return found?.quantity || 0;
    };

    const updateQuantity = (item: App.Data.MenuItemData, delta: number) => {
        const currentQuantity = getItemQuantity(item.id);
        const newQuantity = Math.max(0, currentQuantity + delta);

        if (newQuantity === 0) {
            onItemsChange(selectedItems.filter((si) => si.item.id !== item.id));
        } else {
            const exists = selectedItems.find((si) => si.item.id === item.id);
            if (exists) {
                onItemsChange(
                    selectedItems.map((si) =>
                        si.item.id === item.id
                            ? { ...si, quantity: newQuantity }
                            : si,
                    ),
                );
            } else {
                onItemsChange([
                    ...selectedItems,
                    { item, quantity: newQuantity },
                ]);
            }
        }
    };

    const toggleItem = (item: App.Data.MenuItemData) => {
        const exists = selectedItems.find((si) => si.item.id === item.id);
        if (exists) {
            onItemsChange(selectedItems.filter((si) => si.item.id !== item.id));
        } else {
            onItemsChange([...selectedItems, { item, quantity: 1 }]);
        }
    };

    const totalItems = selectedItems.reduce((sum, si) => sum + si.quantity, 0);
    const totalPrice = selectedItems.reduce(
        (sum, si) => sum + si.item.price * si.quantity,
        0,
    );

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    className="h-auto min-h-[60px] w-full justify-between border-2 border-dashed border-primary/30 bg-transparent transition-all hover:border-primary hover:bg-primary/5"
                >
                    <div className="flex items-center gap-3">
                        <ShoppingBag className="h-5 w-5 text-primary" />
                        <div className="text-left">
                            <p className="font-medium">
                                {totalItems > 0
                                    ? `${totalItems} item${totalItems > 1 ? 's' : ''} selected`
                                    : 'Select Menu Items'}
                            </p>
                            {totalItems > 0 && (
                                <p className="text-sm text-muted-foreground">
                                    {totalPrice.toFixed(2)} EGP total
                                </p>
                            )}
                        </div>
                    </div>
                    <Plus className="h-5 w-5" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden overflow-x-hidden p-0">
                <DialogHeader className="overflow-x-hidden border-b bg-card p-6 pb-4">
                    <DialogTitle className="text-2xl font-bold">
                        Select Menu Items
                    </DialogTitle>
                    <div className="relative mt-4">
                        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search menu items..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <div className="mt-4 w-full max-w-full overflow-x-scroll">
                        <div className="flex max-w-full gap-2 pb-2">
                            {categories.map((category) => (
                                <Button
                                    key={category}
                                    variant={
                                        activeCategory === category
                                            ? 'default'
                                            : 'secondary'
                                    }
                                    size="sm"
                                    onClick={() => setActiveCategory(category)}
                                    className="shrink-0 rounded-full whitespace-nowrap"
                                >
                                    {category}
                                </Button>
                            ))}
                        </div>
                    </div>
                </DialogHeader>
                <ScrollArea className="h-[400px] overflow-x-hidden p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <div className="grid gap-3">
                            {menuItems.map((item) => {
                                const quantity = getItemQuantity(item.id);
                                const isSelected = quantity > 0;

                                return (
                                    <div
                                        key={item.id}
                                        className={`flex cursor-pointer items-center gap-4 rounded-lg border-2 p-4 px-4 transition-all ${
                                            isSelected
                                                ? 'border-primary bg-primary/5'
                                                : 'border-border hover:border-primary/50 hover:bg-muted/50'
                                        }`}
                                        onClick={() => toggleItem(item)}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            className="pointer-events-none"
                                        />
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            width={60}
                                            height={60}
                                            className="rounded-md object-cover"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <h4 className="truncate font-semibold">
                                                    {item.title}
                                                </h4>
                                                <Badge
                                                    variant="secondary"
                                                    className="shrink-0 text-xs"
                                                >
                                                    {item.category?.name ||
                                                        'Uncategorized'}
                                                </Badge>
                                            </div>
                                            <p className="line-clamp-1 text-sm text-muted-foreground">
                                                {item.description}
                                            </p>
                                            <p className="mt-1 text-sm font-semibold text-primary">
                                                {item.price.toFixed(2)} EGP
                                            </p>
                                        </div>
                                        {isSelected && (
                                            <div
                                                className="flex items-center gap-2"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    className="h-8 w-8 bg-transparent"
                                                    onClick={() =>
                                                        updateQuantity(item, -1)
                                                    }
                                                >
                                                    <Minus className="h-4 w-4" />
                                                </Button>
                                                <span className="w-8 text-center font-medium">
                                                    {quantity}
                                                </span>
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    className="h-8 w-8 bg-transparent"
                                                    onClick={() =>
                                                        updateQuantity(item, 1)
                                                    }
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {!loading && menuItems.length === 0 && (
                                <div className="py-12 text-center text-muted-foreground">
                                    <p>No items found matching your search.</p>
                                </div>
                            )}
                        </div>
                    )}
                </ScrollArea>

                <div className="border-t bg-card p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-muted-foreground">
                                {totalItems} item{totalItems !== 1 ? 's' : ''}{' '}
                                selected
                            </p>
                            <p className="text-xl font-bold text-primary">
                                {totalPrice.toFixed(2)} EGP
                            </p>
                        </div>
                        <Button onClick={() => setOpen(false)} size="lg">
                            Confirm Selection
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function SelectedItemsList({
    selectedItems,
    onItemsChange,
}: {
    selectedItems: SelectedItem[];
    onItemsChange: (items: SelectedItem[]) => void;
}) {
    if (selectedItems.length === 0) return null;

    const removeItem = (itemId: string) => {
        onItemsChange(selectedItems.filter((si) => si.item.id !== itemId));
    };

    const updateQuantity = (itemId: string, delta: number) => {
        onItemsChange(
            selectedItems
                .map((si) => {
                    if (si.item.id === itemId) {
                        const newQuantity = si.quantity + delta;
                        return newQuantity > 0
                            ? { ...si, quantity: newQuantity }
                            : null;
                    }
                    return si;
                })
                .filter((si): si is SelectedItem => si !== null),
        );
    };

    const total = selectedItems.reduce(
        (sum, si) => sum + si.item.price * si.quantity,
        0,
    );

    return (
        <div className="mt-4 space-y-3">
            <h3 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
                Your Order
            </h3>
            <div className="space-y-2">
                {selectedItems.map(({ item, quantity }) => (
                    <div
                        key={item.id}
                        className="flex items-center gap-3 rounded-lg bg-muted/50 p-3"
                    >
                        <img
                            src={item.image}
                            alt={item.title}
                            width={48}
                            height={48}
                            className="rounded-md object-cover"
                        />
                        <div className="min-w-0 flex-1">
                            <p className="truncate font-medium">{item.title}</p>
                            <p className="text-sm text-muted-foreground">
                                {item.price.toFixed(2)} EGP each
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => updateQuantity(item.id, -1)}
                            >
                                <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 text-center text-sm font-medium">
                                {quantity}
                            </span>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => updateQuantity(item.id, 1)}
                            >
                                <Plus className="h-3 w-3" />
                            </Button>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() => removeItem(item.id)}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-between border-t pt-3">
                <span className="font-medium">Subtotal</span>
                <span className="text-lg font-bold text-primary">
                    {total.toFixed(2)} EGP
                </span>
            </div>
        </div>
    );
}
